import SeeMoreButton from '@/common/atoms/SeeMoreButton'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import NextStepItem from '@/nextStep/components/NextStepItem'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { usePathInOrg } from '@/org/hooks/usePathInOrg'
import { Box, Collapse, useDisclosure } from '@chakra-ui/react'
import { Next_Step_Bool_Exp, useNextStepsSubscription } from '@gql'
import React, { useMemo } from 'react'
import { Link } from 'react-router'
import { sidebarSubItemProps } from './SidebarItem'
import SidebarItemsContainer from './SidebarItemsContainer'

const MAX = 5

// My next steps still to do, under the « Prochains pas » entry, like the
// tensions and the projects (#122). Same filter as the default view of the
// page (G3-D5): my roles AND what is assigned to me. Each line opens the page,
// where a step is ticked, edited or archived.
export default function SidebarNextSteps() {
  const { orgId, orgData } = useOrgContext()
  const currentMember = useCurrentMember()
  const nextStepsPath = usePathInOrg('next-steps')
  const expanded = useDisclosure()

  const myCirclesIds = useMemo(
    () =>
      currentMember
        ? (orgData?.circlesOfMember(currentMember.id).map((c) => c.id) ?? [])
        : [],
    [orgData, currentMember?.id]
  )

  const where = useMemo<Next_Step_Bool_Exp>(
    () => ({
      _and: [
        { orgId: { _eq: orgId } },
        { archivedAt: { _is_null: true }, done: { _eq: false } },
        {
          _or: [
            { circleId: { _in: myCirclesIds } },
            { memberId: { _eq: currentMember?.id } },
          ],
        },
      ],
    }),
    [orgId, myCirclesIds, currentMember?.id]
  )

  const { data, loading } = useNextStepsSubscription({
    skip: !orgId || !currentMember,
    variables: { where },
  })
  const nextSteps = data?.next_step ?? []
  const hasTail = nextSteps.length > MAX
  const head = hasTail ? nextSteps.slice(0, MAX) : nextSteps
  const tail = hasTail ? nextSteps.slice(MAX) : []

  const renderNextStep = (nextStep: (typeof nextSteps)[number]) => (
    <Box
      key={nextStep.id}
      as={Link}
      to={nextStepsPath}
      display="block"
      py={1}
      pr={2}
      {...sidebarSubItemProps}
    >
      <NextStepItem nextStep={nextStep} showRole={false} />
    </Box>
  )

  return (
    <SidebarItemsContainer hasItems={!loading && nextSteps.length > 0}>
      {head.map(renderNextStep)}
      {hasTail && (
        <Collapse in={expanded.isOpen}>{tail.map(renderNextStep)}</Collapse>
      )}
      {hasTail && (
        <SeeMoreButton
          expanded={expanded.isOpen}
          onToggle={expanded.onToggle}
          itemProps={sidebarSubItemProps}
        />
      )}
    </SidebarItemsContainer>
  )
}
